import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { FormGroup } from '@/components/form/FormGroup';
import { Input } from '@/components/form/Input';
import { useToast } from '@/components/ui/useToast';
import { setReorderLevel, type WarehouseStockRow } from '@/api/warehouseStock';
import { useFieldErrors } from '@/lib/useFieldErrors';
import { apiError } from '@/lib/apiError';

interface Props {
  open: boolean;
  row: WarehouseStockRow | null;
  onClose: () => void;
}

export function SetReorderModal({ open, row, onClose }: Props) {
  const qc = useQueryClient();
  const toast = useToast();
  const fe = useFieldErrors<'reorder'>();
  const [value, setValue] = useState('');

  useEffect(() => {
    if (open && row) setValue(String(row.reorderLevel || ''));
  }, [open, row]);

  const mut = useMutation({
    mutationFn: () =>
      setReorderLevel({ itemId: row!.itemId, reorderLevel: Number(value) }),
    onSuccess: () => {
      toast.push(`Reorder level updated for ${row?.itemName}`, 'success');
      qc.invalidateQueries({ queryKey: ['warehouse-stock'] });
      qc.invalidateQueries({ queryKey: ['warehouse-stock-kpis'] });
      onClose();
    },
    onError: (e) => {
      toast.push(apiError(e, 'Failed'), 'error');
    },
  });

  function submit() {
    const n = Number(value);
    if (!fe.validate({
      reorder: (Number.isNaN(n) || n < 0) && 'Reorder must be a non-negative number',
    })) return;
    mut.mutate();
  }

  return (
    <Modal
      open={open}
      title={row ? `Set Reorder Level — ${row.itemName}` : 'Set Reorder Level'}
      onClose={onClose}
      size="sm"
      footer={
        <>
          <Button variant="glass" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submit} disabled={mut.isPending}>
            {mut.isPending ? 'Saving…' : 'Save'}
          </Button>
        </>
      }
    >
      {row && (
        <FormGroup label={`Reorder level (${row.unit}) — triggers Low Stock`} error={fe.errors.reorder}>
          <Input
            type="number"
            min="0"
            invalid={fe.has('reorder')}
            value={value}
            onChange={(e) => { setValue(e.target.value); fe.clear('reorder'); }}
          />
        </FormGroup>
      )}
    </Modal>
  );
}
